import { compareDesc } from 'date-fns';

import {
  type DraftExpense,
  type DraftExpenses,
  type RawExpense,
} from '@finance-review/models/expense';

import { transformRawExpense } from './transformRawExpense';

export const transformRawExpenses = (
  rawExpenses: RawExpense[],
): DraftExpenses => {
  const expenses = rawExpenses.map((rawExpense) =>
    transformRawExpense(rawExpense),
  );
  const { groupedByRequester, groupedOverLimitByRequester } = expenses.reduce(
    (result, expense) => {
      const requesterId = expense.requester.id;

      const isOverSpendLimit =
        expense.spendLimitStatus &&
        expense.spendLimitStatus.some(
          ({ usedAmount, limitAmount }) =>
            usedAmount && limitAmount && usedAmount > limitAmount,
        );

      const group = isOverSpendLimit
        ? result.groupedOverLimitByRequester
        : result.groupedByRequester;

      if (!group[requesterId]) {
        group[requesterId] = [];
      }
      group[requesterId].push(expense);

      return result;
    },
    { groupedByRequester: {}, groupedOverLimitByRequester: {} } as {
      groupedByRequester: Record<string, DraftExpense[]>;
      groupedOverLimitByRequester: Record<string, DraftExpense[]>;
    },
  );

  return {
    expenses,
    expensesByRequester: getFormattedGroup(groupedByRequester),
    expensesOverSpendLimitByRequester: getFormattedGroup(
      groupedOverLimitByRequester,
    ),
  };
};

const getFormattedGroup = (
  groupedByRequester: Record<string, DraftExpense[]>,
) => {
  return Object.keys(groupedByRequester)
    .map((userId) => {
      const requesterExpenses = groupedByRequester[userId].sort((a, b) =>
        a.expenseDate && b.expenseDate
          ? compareDesc(a.expenseDate, b.expenseDate)
          : 0,
      );

      return {
        requester: requesterExpenses[0].requester,
        expenses: requesterExpenses,
      };
    })
    .sort((expensesByRequester1, expensesByRequester2) => {
      return expensesByRequester1.requester.fullname.localeCompare(
        expensesByRequester2.requester.fullname,
      );
    });
};
